import React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// components
import { Button } from 'components/UI'
import PageLoader from 'components/UI/Loader/PageLoader'
import { CardContent, Typography } from '@mui/material'
// graphql
import { AUTH_TWO_FACTOR_STATUS_QUERY } from 'graphql/auth/twoFactor/queries'
// constants
import { Modals } from 'constants/modals'
// views
import {
  Status,
  InfoText,
  EnabledAt,
  PageTitle,
  StatusText,
  ButtonGroup,
  SectionCard,
  SectionTitle,
  PageContainer
} from '../shared/views'
// components (local)
import SessionList from './components/SessionsList'
// types
import { IAuthTwoFactorStatus } from 'types/graphql'

const SecurityPage: React.FC = () => {
  const { showModal: showSetupModal } = useShowModal({
    modalType: Modals.TWO_FACTOR_SETUP_MODAL
  })
  const { showModal: showDisableModal } = useShowModal({
    modalType: Modals.TWO_FACTOR_DISABLE_MODAL
  })
  const { showModal: showRegenerateModal } = useShowModal({
    modalType: Modals.TWO_FACTOR_REGENERATE_CODES_MODAL
  })

  const { data, loading, refetch } = useQuery<IAuthTwoFactorStatus>(AUTH_TWO_FACTOR_STATUS_QUERY, {
    fetchPolicy: 'network-only'
  })

  const twoFactorEnabled = data?.authTwoFactorStatus?.data?.enabled || false
  const enabledAt = data?.authTwoFactorStatus?.data?.enabledAt

  const handleEnableTwoFactor = () => {
    showSetupModal({ refetch })
  }

  const handleDisableTwoFactor = () => {
    showDisableModal({ refetch })
  }

  const handleRegenerateCodes = () => {
    showRegenerateModal({ refetch })
  }

  if (loading) {
    return <PageLoader isLoading={loading} height='400px' />
  }

  return (
    <PageContainer>
      <PageTitle>Security Settings</PageTitle>

      <SectionCard>
        <CardContent>
          <SectionTitle>Two-Factor Authentication</SectionTitle>

          <InfoText>
            Two-factor authentication (2FA) adds an extra layer of security to your account. When enabled, you'll need
            to enter a verification code from your authenticator app in addition to your password when signing in.
          </InfoText>

          <Status>
            Status:
            <StatusText enabled={twoFactorEnabled}>{twoFactorEnabled ? ' Enabled' : ' Disabled'}</StatusText>
          </Status>

          {twoFactorEnabled && enabledAt && <EnabledAt>Enabled on {new Date(enabledAt).toLocaleString()}</EnabledAt>}

          <ButtonGroup>
            {!twoFactorEnabled ? (
              <Button variant='contained' onClick={handleEnableTwoFactor}>
                Enable 2FA
              </Button>
            ) : (
              <>
                <Button variant='outlined' onClick={handleDisableTwoFactor}>
                  Disable 2FA
                </Button>
                <Button variant='outlined' onClick={handleRegenerateCodes}>
                  Regenerate Backup Codes
                </Button>
              </>
            )}
          </ButtonGroup>

          {!twoFactorEnabled && (
            <div style={{ marginTop: '20px' }}>
              <Typography variant='h6' gutterBottom>
                How it works:
              </Typography>
              <Typography variant='body2' component='div'>
                <ol>
                  <li>Download an authenticator app (e.g., Google Authenticator, Microsoft Authenticator)</li>
                  <li>Click "Enable Two-Factor Authentication" above</li>
                  <li>Scan the QR code with your authenticator app</li>
                  <li>Save your backup codes in a safe place</li>
                  <li>Enter the 6-digit code from your app to confirm setup</li>
                </ol>
              </Typography>
            </div>
          )}
        </CardContent>
      </SectionCard>

      <SessionList />
    </PageContainer>
  )
}

export default SecurityPage
